import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

/** Known feature flag names */
export type FeatureFlag = "beta_features" | "debug_banner" | "spond_integration" | "ai_coach_child" | "ai_coach_parent";

/** Flags users can toggle themselves in Settings. Others are admin-only (set via database). */
export const SELF_SERVICE_FLAGS: FeatureFlag[] = ["ai_coach_child", "ai_coach_parent"];
export const ADMIN_ONLY_FLAGS: FeatureFlag[] = ["beta_features", "debug_banner", "spond_integration"];

export function useFeatureFlags() {
  const { user } = useAuth();

  const { data: flags = [], isLoading } = useQuery({
    queryKey: ["feature-flags", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag, enabled")
        .eq("user_id", user.id);
      if (error) throw error;
      return data as { flag: string; enabled: boolean }[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const enabledFlags = new Set(
    flags.filter((f) => f.enabled).map((f) => f.flag)
  );

  function hasFlag(flag: FeatureFlag): boolean {
    return enabledFlags.has(flag);
  }

  async function toggleFlag(flag: FeatureFlag, enabled: boolean) {
    if (!user) return;
    const { error } = await supabase
      .from("feature_flags")
      .upsert({ user_id: user.id, flag, enabled }, { onConflict: "user_id,flag" });
    if (error) throw error;
  }

  return { hasFlag, isLoading, flags, toggleFlag };
}
